import { useState } from 'react';
import { MapPin, X } from 'lucide-react';
import type { Translations, GeoLocation, Language } from '../types';
import { events } from '../lib/firebase';
import {
  resolveVideoSources,
  HERO_LOOP_SRC,
  HERO_POSTER_SRC,
  HERO_ALT,
} from '../lib/heroVideo';
import { useReducedMotion } from '../lib/useReducedMotion';

interface HeroProps {
  t: Translations;
  /** #1967 R1: selects the video cut. `t` carries the strings but not the tag. */
  language: Language;
  location: GeoLocation;
  onGetStarted: () => void;
  isMaintenanceMode?: boolean;
}

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.quul';
// Sprint 81 BUG-81-08 §3: App Store URL. Public listing live at
// apps.apple.com/us/app/quul/id6761440078 (post-Apple-review). The earlier
// PLACEHOLDER TestFlight URL was a pre-review fallback; replaced with the
// real App Store URL once Jose confirmed the listing was live (BUG-81-08
// follow-up).
const APP_STORE_URL = 'https://apps.apple.com/us/app/quul/id6761440078';

export function Hero({ t, language, location, onGetStarted, isMaintenanceMode = false }: HeroProps) {
  // #1967 R1: Learn More modal state. The <video> element only mounts while the
  // modal is open, so nothing is fetched on page load.
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const videoSources = resolveVideoSources(language);
  // #1967 R6: the hero loop is decorative motion, so it yields to the
  // visitor's OS-level reduced-motion preference.
  const prefersReducedMotion = useReducedMotion();
  const locationDisplay = location.loading
    ? t.detectingLocation
    : location.error
    ? t.locationUnavailable
    : `${location.city}, ${location.region}`;

  const handlePlayStoreClick = () => {
    events.appStoreClick('google_play');
  };

  const handleAppStoreClick = () => {
    events.appStoreClick('app_store');
  };

  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Text Content */}
          <div className="space-y-4 sm:space-y-6">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {t.heroTitle1}
              <br />
              <span className="text-vinotinto">{t.heroTitle2}</span>
            </h1>
            <p className="text-base sm:text-xl text-gray-600">{t.heroDescription}</p>

            {/* Location Detection */}
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="w-4 h-4 text-vinotinto" />
              <span>{t.detected}</span>
              <span className="font-medium text-gray-900">{locationDisplay}</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={isMaintenanceMode ? undefined : onGetStarted}
                disabled={isMaintenanceMode}
                className={`px-6 py-3 rounded-lg transition-colors font-medium ${
                  isMaintenanceMode
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-vinotinto text-white hover:bg-vinotinto-dark'
                }`}
              >
                {isMaintenanceMode ? t.comingSoon : t.joinCommunity}
              </button>
              <button
                onClick={() => setIsVideoOpen(true)}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:border-vinotinto hover:text-vinotinto transition-colors font-medium"
              >
                {t.learnMore}
              </button>
            </div>

            {/* App Store Badges — Google Play LIVE, App Store in review */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-4 sm:mt-6">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handlePlayStoreClick}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-vinotinto-dark transition-colors"
                aria-label={`${t.getOnGooglePlay} ${t.googlePlay}`}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] opacity-90">{t.getOnGooglePlay}</div>
                  <div className="text-sm font-semibold">{t.googlePlay}</div>
                </div>
              </a>

              {/* Sprint 81 BUG-81-08 §3: App Store badge enabled. Currently
                  links to TestFlight while production iOS is pending Apple
                  review (Stream W v2.1.0 build). Update APP_STORE_URL once
                  the public App Store listing is live. */}
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleAppStoreClick}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-vinotinto-dark transition-colors"
                aria-label={`${t.getOnAppStore} ${t.appStore}`}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] opacity-90">{t.getOnAppStore}</div>
                  <div className="text-sm font-semibold">{t.appStore}</div>
                </div>
              </a>
            </div>
          </div>

          {/* Right - Hero. #1967 R6: an 8 s loop with the still as its poster
              (founder decision 2026-09-09), served from public/assets/ rather
              than the third-party Unsplash hotlink this replaces. A visitor who
              has asked for reduced motion gets the poster still on its own —
              the same picture, held. */}
          <div className="relative">
            {prefersReducedMotion ? (
              <img
                src={HERO_POSTER_SRC}
                alt={HERO_ALT}
                className="rounded-2xl shadow-2xl w-full object-cover"
                loading="lazy"
              />
            ) : (
              <video
                src={HERO_LOOP_SRC}
                poster={HERO_POSTER_SRC}
                aria-label={HERO_ALT}
                autoPlay
                muted
                loop
                playsInline
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
            )}
          </div>
        </div>
      </div>

      {/* Video modal behind the Learn More CTA (Sprint 81 BUG-81-08 §2). */}
      {isVideoOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t.learnMore}
          onClick={() => setIsVideoOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-8 h-8" />
            </button>
            {/* #1967 R2: one <source> per rendition, highest first, each
                carrying its measured codec profile. A bare type="video/mp4"
                on both would make the browser take the first and never
                evaluate the second. */}
            <video controls autoPlay className="w-full rounded-lg shadow-2xl">
              {videoSources.map((source) => (
                <source key={source.src} src={source.src} type={source.type} />
              ))}
              Tu navegador no soporta video HTML5.
            </video>
          </div>
        </div>
      )}
    </section>
  );
}
